import { BpmDateString } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  Forecast,
  ForecastGroup,
  ForecastKpiViewOption,
  ForecastModelSummaryData,
  ForecastResultsData,
  HistoricalData,
  OutcomeVariableType,
  TrainTestData
} from '@blisspointmedia/bpm-types/dist/Forecasting';
import { abbreviateNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  CPM_FORMATTER,
  SPEND_OR_REVENUE_FORMATTER
} from '@blisspointmedia/bpm-types/dist/metricFormatUtils';

export const PageTab = {
  FORECAST_ONBOARDING: 'forecast-onboarding',
  REQUEST_FORECAST: 'request-forecast',
  SAVED_FORECASTS: 'saved-forecasts',
  FORECAST_RESULTS: 'forecast-results',
  SCALING_RESULTS: 'scaling-results',
  BUDGET_BUILDER: 'budget-builder',
  DATA_QA: 'data-qa'
} as const;
export type PageTabValue = (typeof PageTab)[keyof typeof PageTab];

// S3 location the R Data QA pipeline writes to: the modal lists/opens the HTML
// reports here and useForecastDataQa reads the results JSON from the same
// prefix.
export const DATA_QA_S3_BUCKET = 'bpm-econ';
export const DATA_QA_S3_PREFIX = 'bppm/econ_bigsix_forecasting/output';

// S3 location the R forecasting pipeline writes each forecast's budget-builder
// saturation curves to.
export const BUDGET_BUILDER_S3_BUCKET = 'bpm-econ';
export const BUDGET_BUILDER_S3_PREFIX = 'bppm/econ_bigsix_forecasting/data';

export type GraphViewOptions = 'showGraph' | 'showTable';

// The forecasting scope a client saved during onboarding (channels + KPIs),
// loaded from /loadDataQaConfig. The Request Forecast page narrows its menu to
// this scope; an absent/empty config falls back to the full QA universe.
export interface DataQaConfig {
  channels: string[];
  kpis: string[];
}

// /getForecastChannels result: channel → platform → KPI → tactics with
// cross-channel data for this client (the lambda builds
// results[channel][platform][kpi] = tactic[]; tactics omit blank/"N/A").
export type ForecastDataTree = Record<string, Record<string, Record<string, string[]>>>;

export type BudgetViewOptions = 'showPercentage' | 'showDollars';

export const OUTCOME_VARIABLE_LABELS: Record<OutcomeVariableType, string> = {
  Volume: 'KPI Volume',
  Revenue: 'Revenue KPI'
};

export const ForecastChartViewLabels: Record<
  OutcomeVariableType,
  Record<ForecastKpiViewOption, string>
> = {
  Volume: {
    [ForecastKpiViewOption.TOTAL]: OUTCOME_VARIABLE_LABELS.Volume,
    [ForecastKpiViewOption.EFFICIENCY]: 'CPA'
  },
  Revenue: {
    [ForecastKpiViewOption.TOTAL]: OUTCOME_VARIABLE_LABELS.Revenue,
    [ForecastKpiViewOption.EFFICIENCY]: 'ROAS'
  }
};

export interface ForecastingNav {
  label: string;
  key: PageTabValue;
  // Shown only with the enableForecastingSelfServe experiment.
  experimental?: boolean;
  // Shown only to internal (Tinuiti) users, whatever the company's experiments.
  internalOnly?: boolean;
}

export const NAVS: ForecastingNav[] = [
  { label: 'Forecast Onboarding', key: PageTab.FORECAST_ONBOARDING, experimental: true },
  { label: 'Request Forecast', key: PageTab.REQUEST_FORECAST, experimental: true },
  // Internal users only (product decision, 2026-11-03).
  { label: 'Saved Forecasts', key: PageTab.SAVED_FORECASTS, internalOnly: true },
  { label: 'Forecast Results', key: PageTab.FORECAST_RESULTS },
  // Same self-serve gate as Request Forecast: anyone who can request scaling sees the results.
  { label: 'Scaling Results', key: PageTab.SCALING_RESULTS, experimental: true },
  { label: 'Budget Builder', key: PageTab.BUDGET_BUILDER },
  { label: 'Data QA', key: PageTab.DATA_QA, experimental: true }
];

export type FullForecastSelection = Omit<ForecastGroup, 'forecasts'> &
  Omit<Forecast, 'kpis'> & {
    kpiName: string;
    outcomeVar: OutcomeVariableType;
  };

type RangeForecastValue = 'confidenceInterval' | 'efficiencyConfidenceInterval';
type NumericForecastValues = 'kpi' | 'kpiEfficiency' | 'spend' | 'cpm';
export type FlatForecastData<BudgetName extends string> = {
  date: BpmDateString;
  timestamp: number; // Milliseconds since epoch, used for charting on the x-axis
} & Record<`${BudgetName}-${NumericForecastValues}`, number> &
  Partial<Record<`${BudgetName}-${RangeForecastValue}`, [number, number]>>;

export interface OutcomeKpiForecastData {
  trainTestData: TrainTestData[];
  historicalData: HistoricalData[];
  forecastResultsData: ForecastResultsData[];
  modelSummaryData: ForecastModelSummaryData;
}

export interface FlattenedForecastDataResult {
  trainTestData: FlatForecastData<string>[];
  historicalData: FlatForecastData<string>[];
  forecastResultsData: FlatForecastData<string>[];
}

interface TimestampRange {
  min: number;
  max: number;
}
export interface ForecastDataTimestampRanges {
  trainTestTimestampRange: TimestampRange;
  historicalTimestampRange: TimestampRange;
  forecastResultsTimestampRange: TimestampRange;
}

export const SelectableForecastProperty = {
  CHANNEL_PLATFORM: 'channel-platform',
  OUTCOME_VARIABLE: 'outcome-variable',
  KPI: 'kpi',
  // Value is the forecast group's groupId, not its formatted date-range text —
  // two groups can share displayed dates, so groupId is the actual identity.
  FORECAST_GROUP: 'forecast-group'
} as const;
export type SelectableForecastPropertyValue =
  (typeof SelectableForecastProperty)[keyof typeof SelectableForecastProperty];

interface ForecastPropertyTypesMap {
  [SelectableForecastProperty.CHANNEL_PLATFORM]: string;
  [SelectableForecastProperty.OUTCOME_VARIABLE]: OutcomeVariableType;
  [SelectableForecastProperty.KPI]: string;
  [SelectableForecastProperty.FORECAST_GROUP]: string;
}
export type ForecastPropertyValueType<P extends SelectableForecastPropertyValue> =
  ForecastPropertyTypesMap[P];

export type MatchableForecastData = {
  [K in keyof ForecastPropertyTypesMap]: ForecastPropertyTypesMap[K];
} & { forecastId: string };

export type MatchableForecastOptions = {
  [K in keyof ForecastPropertyTypesMap]: ForecastPropertyTypesMap[K][];
};

export const DEFAULT_COMPARISON_BUDGET_PERCENTAGES: number[] = [-75, -50, -25, 25, 50, 75, 100];
export const HISTORICAL_KEY = 'historical';
export const GAP_KEY = 'gap';
export const EMPTY_VALUE = '--';

export const formatRawBudget = (val: number): string => `$${abbreviateNumber(val)}`;
export const formatPercentBudget = (val: number): string => `${abbreviateNumber(val * 100)}%`;
export const formatSpend = (val: number): string =>
  SPEND_OR_REVENUE_FORMATTER(val, { zeroLabel: '0' });
export const formatAxisSpend = (val: number): string => `$${abbreviateNumber(val)}`;
export const formatCpm = (val: number): string => CPM_FORMATTER(val, { zeroLabel: '0' });
export const formatCpmRounded = (val: number): string =>
  CPM_FORMATTER(val, { decimals: 0, zeroLabel: '0' });
